import { createContext, ReactElement, useEffect, useState } from 'react';
import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import { Database, equalTo, get, getDatabase, limitToFirst, onChildAdded, onChildRemoved, onValue, orderByChild, orderByKey, push, query, ref, runTransaction, set, startAfter, update } from 'firebase/database';
import { Auth, browserSessionPersistence, getAuth, onAuthStateChanged, setPersistence, signInWithCustomToken, signOut } from 'firebase/auth';
import { CallStatus, FirebaseContextType, IMessage, UserActivity } from 'types/auth';
import { getFirebaseToken, subscribeFirebaseToken } from 'utils/firebaseTokenBus';

const FirebaseContext = createContext<FirebaseContextType | null>(null);

export const FirebaseProvider = ({ children }: { children: ReactElement }) => {
  const [app, setApp] = useState<FirebaseApp | null>(null);
  const [database, setDatabase] = useState<Database | null>(null);
  const [auth, setAuth] = useState<Auth | null>(null);
  // The signed-in Firebase uid (null until signInWithCustomToken resolves).
  // RTDB listeners must gate on this so they never attach before auth — which
  // would be denied by the security rules — and so they key off the real uid.
  const [firebaseUid, setFirebaseUid] = useState<string | null>(null);

  useEffect(() => {
    const firebaseConfig = {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_FIREBASE_APP_ID
    };

    const firebaseApp = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
    const db = getDatabase(firebaseApp);
    const fbAuth = getAuth(firebaseApp);
    // Keep the Firebase sign-in per tab and gone when the browser closes, like the
    // dashboard session itself (it used to persist in IndexedDB indefinitely). A user
    // already persisted there is moved into this tab's storage.
    setPersistence(fbAuth, browserSessionPersistence).catch((err) =>
      console.error('[Firebase Auth] could not switch to session persistence:', err)
    );

    setApp(firebaseApp);
    setDatabase(db);
    setAuth(fbAuth);

    console.log(firebaseApp.name, 'Firebase initialized ✅');
  }, []);

  useEffect(() => {
    if (!auth) return;

    // undefined = nothing applied yet, so a tab that starts without a token still
    // clears a Firebase sign-in left over from an earlier session.
    let currentToken: string | null | undefined = undefined;

    const applyToken = async (token: string | null) => {
      if (token === currentToken) return;
      currentToken = token;
      try {
        if (token) {
          await signInWithCustomToken(auth, token);
        } else {
          await auth.authStateReady();
          // A token may have arrived while we waited; never sign that one out.
          if (currentToken === null && auth.currentUser) await signOut(auth);
        }
      } catch (err) {
        console.error('[Firebase Auth] signIn/signOut failed:', err);
      }
    };

    const logAuthState = onAuthStateChanged(auth, (user) => {
      console.log('[Firebase Auth] state changed:', user ? `uid=${user.uid}` : 'signed out');
      setFirebaseUid(user?.uid ?? null);
    });

    void applyToken(getFirebaseToken());
    const unsubscribe = subscribeFirebaseToken((token) => {
      void applyToken(token);
    });

    return () => {
      unsubscribe();
      logAuthState();
    };
  }, [auth]);

  const addCallData = async (data: any, txnId: string) => {
    const videoCallRef = ref(database!, `video-calls/${txnId}`);
    await set(videoCallRef, data);

    // Write a tiny per-recipient pointer so each recipient detects this incoming
    // call by reading only their own `call-recipients/{uid}` node — instead of
    // every client subscribing to the entire `video-calls` collection (the
    // whole-node read that drove the RTDB bill).
    try {
      const recipients: string[] = Array.isArray(data?.recipients) ? data.recipients : [];
      const initiated = typeof data?.initiated === 'number' ? data.initiated : Date.now();
      await Promise.all(
        recipients
          .filter((uid) => typeof uid === 'string' && uid.length >= 16)
          .map((uid) =>
            set(ref(database!, `call-recipients/${uid}/${txnId}`), { callId: txnId, initiated })
          )
      );
    } catch (err) {
      console.error('Failed to write call-recipients pointers:', err);
    }
    return txnId;
  };

  const getCallData = async (callId: string) => {
    const videoCallRef = ref(database!, `video-calls/${callId}`);
    const snapshot = await get(videoCallRef);

    if (snapshot.exists()) {
      return snapshot.val();
    } else {
      return null;
    }
  };

  const updateCallStatus = async (callId: string, newStatus: CallStatus) => {
    const callRef = ref(database!, `video-calls/${callId}`);
    await update(callRef, { status: newStatus });
  };

  const updateCallSupportInitiatedBy = async (callId: string, supportAccepted: string) => {
    const callRef = ref(database!, `video-calls/${callId}`);
    await update(callRef, { supportAccepted });
  };

  const updateRecipients = async (callId: string, recipients: string[]) => {
    const callRef = ref(database!, `video-calls/${callId}`);
    const snapshot = await get(callRef);
    if (!snapshot.exists()) {
      return;
    }
    await update(callRef, { recipients });
  };

  const updateSessionData = async (callId: string, sessionData: any) => {
    const callRef = ref(database!, `video-calls/${callId}`);
    await update(callRef, { session: sessionData });
  };

  /**
   * Append to the call's history atomically.
   *
   * This was a read-then-write of the whole array. Both the dashboard and the
   * app append to it (join, mute, camera, leave), so two entries landing
   * together meant the second silently overwrote the first — and a call with no
   * `history` yet threw on the spread. The array shape is kept deliberately:
   * every reader on both sides treats history as a list.
   */
  const addCallHistoryEntry = async (callId: string, history: any) => {
    const historyRef = ref(database!, `video-calls/${callId}/history`);
    await runTransaction(historyRef, (current) => {
      // RTDB returns an array node as an array only while its keys are a
      // contiguous 0..n; delete one entry and the same node comes back as a
      // keyed object. Treating that as empty would silently wipe the history.
      const list = Array.isArray(current)
        ? current
        : current && typeof current === 'object'
          ? Object.keys(current)
              .sort((a, b) => Number(a) - Number(b))
              .map((k) => (current as Record<string, any>)[k])
          : [];
      return [...list, history];
    });
  };

  const addCallToUser = async (callId: string, userId: string, status: CallStatus, userType: string) => {
    const callRef = ref(database!, `video-calls/${callId}`);
    const snapshot = await get(callRef);
    if (!snapshot.exists()) {
      return;
    }
    const callData = snapshot.val();
    const updatedTo = [...(callData.to ? callData.to : []), { user: userId, status, userType }];
    await update(callRef, { to: updatedTo });

    // Ensure a user added mid-call also gets a scoped pointer so their call
    // queue picks it up without scanning the whole `video-calls` node.
    try {
      if (typeof userId === 'string' && userId.length >= 16) {
        const initiated = typeof callData?.initiated === 'number' ? callData.initiated : Date.now();
        await set(ref(database!, `call-recipients/${userId}/${callId}`), { callId, initiated });
      }
    } catch (err) {
      console.error('Failed to write call-recipients pointer (addCallToUser):', err);
    }
  };

  const addSessionUsers = async (callId: string, userId: string) => {
    const callRef = ref(database!, `video-calls/${callId}`);
    const snapshot = await get(callRef);
    if (!snapshot.exists()) {
      return;
    }
    const callData = snapshot.val();
    let { session } = callData;

    const updatedJoinees = [...(session.joinees ? session.joinees : []), userId];
    session.joinees = updatedJoinees;
    await update(callRef, { session });
  };

  const getMyOngoingCalls = async (userId: string) => {
    const videoCallsRef = ref(database!, 'video-calls');

    try {
      const ongoingQuery = query(videoCallsRef, orderByChild('status'), equalTo(CallStatus.IN_PROGRESS));
      const snapshot = await get(ongoingQuery);

      if (!snapshot.exists()) {
        console.log('No ongoing calls found.');
        return [];
      }

      // Filter only calls where userId is in "to" object
      const ongoingCalls = Object.entries(snapshot.val())
        .filter(([_, call]: any) => call.to && call.to.includes(userId))
        .map(([id, call]: any) => ({ id, ...call }));

      console.log('Ongoing calls for user:', ongoingCalls);
      return ongoingCalls;
    } catch (error) {
      console.error('Error fetching ongoing calls:', error);
      return [];
    }
  };
  const updateMuteStatus = async (callId: string, supportMuted: boolean) => {
    const callRef = ref(database!, `video-calls/${callId}`);
    const snapshot = await get(callRef);
    if (!snapshot.exists()) {
      return;
    }
    await update(callRef, { supportMuted });
  };

  const addScreenshot = async (callId: string, screenshot: any) => {
    const callRef = ref(database!, `video-calls/${callId}`);
    const snapshot = await get(callRef);
    if (!snapshot.exists()) {
      return;
    }
    const callData = snapshot.val();
    let { session } = callData;

    const screenshots = [...(session.screenshots ? session.screenshots : []), screenshot];
    await update(callRef, { screenshots });
  };

  const updateLocationStatus = async (callId: string, initGetLocation: boolean) => {
    const callRef = ref(database!, `video-calls/${callId}`);
    const snapshot = await get(callRef);
    if (!snapshot.exists()) {
      return;
    }
    await update(callRef, { initGetLocation });
  };

  const updateRecordingStatus = async (callId: string, isRecording: boolean) => {
    const callRef = ref(database!, `video-calls/${callId}`);
    const snapshot = await get(callRef);
    if (!snapshot.exists()) {
      return;
    }
    await update(callRef, { isRecording });
  };

  const updateFromName = async (callId: string, fromName: string) => {
    const callRef = ref(database!, `video-calls/${callId}`);
    const snapshot = await get(callRef);
    if (!snapshot.exists()) {
      return;
    }
    await update(callRef, { fromName });
  };

  const pushMessage = async (callId: string, message: IMessage) => {
    const messagesRef = ref(database!, `messages/${callId}`);
    await push(messagesRef, message);
  };

  const markMessagesAsRead = async (callId: string, uid: string) => {
    const callRef = ref(database!, `messages/${callId}`);
    const snapshot = await get(callRef);

    if (!snapshot.exists()) {
      console.log('No messages found.');
      return;
    }

    let updates: any = {};
    snapshot.forEach((childSnapshot) => {
      const messageId = childSnapshot.key;
      const messageData = childSnapshot.val();

      // Check if `myUid` is already in `readBy`
      if (!messageData.readBy.includes(uid)) {
        updates[`messages/${callId}/${messageId}/readBy`] = [...messageData.readBy, uid];
      }
    });

    // Apply batch update
    if (Object.keys(updates).length > 0) {
      await update(ref(database!), updates);
    }
  };

  const getMessageHistory = async (callId: string) => {
  const callRef = ref(database!, `messages/${callId}`);
  const snapshot = await get(callRef);

  if (!snapshot.exists()) {
    console.log("No messages found.");
    return [];
  }

  const messages: any[] = [];
  snapshot.forEach((childSnapshot) => {
    const messageId = childSnapshot.key;
    const messageData = childSnapshot.val();
    messages.push({
      id: messageId,
      ...messageData,
    });
  });

  return messages;
};

  const updateSessionTokens = async (callId: string, token: { token: string; uid: string; uidNumber: string }) => {
    const callRef = ref(database!, `video-calls/${callId}`);
    const snapshot = await get(callRef);
    if (!snapshot.exists()) {
      return;
    }
    const callData = snapshot.val();
    let { session } = callData;

    session.tokens.push(token);

    await update(callRef, { session });
  };

  const removeUserUidFromTo = async (callId: string, uid: string) => {
    const callRef = ref(database!, `video-calls/${callId}`);
    const snapshot = await get(callRef);
    if (!snapshot.exists()) {
      return;
    }
    const callData = snapshot.val();
    let { to } = callData;

    to = to ? to.filter((t: any) => t.user != uid) : [];

    await update(callRef, { to });
  };

  const updateUserCallStatus = async (callId: string, uid: string, newStatus: CallStatus) => {
    const callRef = ref(database!, `video-calls/${callId}`);
    const snapshot = await get(callRef);
    if (!snapshot.exists()) {
      return;
    }
    const callData = snapshot.val();
    let { to } = callData;

    if (!to || to.length == 0) return;

    console.log(to, uid, 'totototo-totototototo');

    let meTo = to.find((t: any) => t == uid);
    let notMeTo = to ? to.filter((t: any) => t != uid) : [];

    if (meTo) meTo.status = newStatus;

    await update(callRef, { to: [...notMeTo, ...(meTo ? [meTo] : [])] });
  };

  const getLocationData = async (txnId: string, userId: string) => {
    const refPath = ref(database!, `locations/${txnId}/${userId}`);
    const snapshot = await get(refPath);
    if (!snapshot.exists()) return null;
    return snapshot.val();
  };

  // Read the entire `locations/{txnId}` node — returns { [userId]: trackingDoc }.
  // Used by the consolidated tracking map to render every driver on one canvas.
  const getAllUsersLocationData = async (txnId: string): Promise<Record<string, any> | null> => {
    const refPath = ref(database!, `locations/${txnId}`);
    const snapshot = await get(refPath);
    if (!snapshot.exists()) return null;
    return snapshot.val();
  };

  // Push-based subscription to `locations/{txnId}`. Returns an unsubscribe fn so
  // callers can clean up in useEffect. Yields the SAME `{ [uid]: node }` shape as
  // before — but streamed INCREMENTALLY: the previous `onValue` on the parent
  // re-downloaded the entire (ever-growing) node on every new GPS point, which
  // was the dominant RTDB-egress cost. Here each user's trail arrives point-by-
  // point (existing points once on attach, then only new points), so the map's
  // full trail is preserved while egress drops from O(points) per tick to O(1).
  const subscribeToTransactionLocations = (
    txnId: string,
    onUpdate: (data: Record<string, any> | null) => void
  ) => {
    if (!database) return () => {};
    const db = database;
    const store: Record<string, any> = {};
    const perUser: Record<string, () => void> = {};

    const emit = () => {
      onUpdate(Object.keys(store).length > 0 ? { ...store } : null);
    };

    const parentRef = ref(db, `locations/${txnId}`);

    const onUserAdded = onChildAdded(parentRef, (userSnap) => {
      const uid = userSnap.key;
      if (!uid || perUser[uid]) return;
      const initial = userSnap.val() || {};

      // Seed metadata + the history already delivered in this (one-time) snapshot,
      // then stream ONLY points added afterwards — so history is never re-fetched.
      const rawHist = initial.history;
      const seedHistory: Record<string, any> =
        rawHist && typeof rawHist === 'object' ? { ...rawHist } : {};
      store[uid] = {
        status: initial.status,
        startLocation: initial.startLocation,
        endLocation: initial.endLocation,
        eventUid: initial.eventUid,
        history: seedHistory
      };
      emit();

      const lastKey =
        rawHist && typeof rawHist === 'object' && !Array.isArray(rawHist)
          ? Object.keys(rawHist).sort().pop() || null
          : null;
      const historyRef = ref(db, `locations/${txnId}/${uid}/history`);
      const historyQuery = lastKey ? query(historyRef, orderByKey(), startAfter(lastKey)) : historyRef;
      const offHistory = onChildAdded(historyQuery, (pointSnap) => {
        const rec = store[uid];
        if (!rec || !pointSnap.key) return;
        rec.history = { ...rec.history, [pointSnap.key]: pointSnap.val() };
        emit();
      });

      // Small, rarely-changing metadata kept in sync (e.g. status active→completed).
      const offStatus = onValue(ref(db, `locations/${txnId}/${uid}/status`), (s) => {
        if (store[uid]) { store[uid].status = s.val(); emit(); }
      });
      const offStart = onValue(ref(db, `locations/${txnId}/${uid}/startLocation`), (s) => {
        if (store[uid]) { store[uid].startLocation = s.val(); emit(); }
      });
      const offEnd = onValue(ref(db, `locations/${txnId}/${uid}/endLocation`), (s) => {
        if (store[uid]) { store[uid].endLocation = s.val(); emit(); }
      });

      perUser[uid] = () => { offHistory(); offStatus(); offStart(); offEnd(); };
    });

    const onUserRemoved = onChildRemoved(parentRef, (userSnap) => {
      const uid = userSnap.key;
      if (!uid) return;
      perUser[uid]?.();
      delete perUser[uid];
      delete store[uid];
      emit();
    });

    // onChildAdded never fires for an empty/absent node, so a transaction with
    // no live tracking data would leave consumers waiting forever (e.g. the map's
    // loading spinner never clears). Probe once (at most one child) to guarantee
    // an initial emit — which resolves to null when there's no tracking data.
    get(query(parentRef, limitToFirst(1)))
      .then(() => emit())
      .catch(() => emit());

    return () => {
      onUserAdded();
      onUserRemoved();
      Object.values(perUser).forEach((fn) => fn());
    };
  };

  /**
   * Live presence for a set of users — `user-activity/{uid}`, the heartbeat the
   * Flutter app writes every couple of minutes (foreground AND from the
   * background tracking isolate).
   *
   * This is the only thing that separates "driver stopped moving" from "driver's
   * phone died": a tracking node stays `active` and its last GPS point stays put
   * in both cases. The map previously had no access to it at all, so a dead
   * device kept a pulsing live marker.
   *
   * One listener per uid, not one on the `user-activity` parent: the parent
   * holds every user in the tenant, and subscribing to it would stream the whole
   * fleet's heartbeats to every open transaction page. Each child node is two
   * scalars, so the per-uid fan-out is the cheap direction.
   *
   * Reads require `auth != null` under the RTDB rules, so callers must gate on
   * `firebaseUid` before subscribing.
   */
  const subscribeToUserActivity = (
    uids: string[],
    onUpdate: (data: Record<string, UserActivity>) => void
  ) => {
    if (!database || uids.length === 0) return () => {};
    const db = database;
    const store: Record<string, UserActivity> = {};

    const parseTs = (raw: any): number | null =>
      typeof raw === 'number' ? raw : typeof raw === 'string' ? Date.parse(raw) || null : null;

    const parseAppState = (raw: any): UserActivity['appState'] =>
      raw === 'foreground' || raw === 'tracking' || raw === 'background' ? raw : null;

    const offs = uids
      .filter((uid) => typeof uid === 'string' && uid.length > 0)
      .map((uid) =>
        onValue(
          ref(db, `user-activity/${uid}`),
          (snap) => {
            const v: any = snap.val();
            store[uid] = {
              // The app writes ServerValue.timestamp (a number), but tolerate an
              // ISO string so an older client never reads as permanently offline.
              lastSeenAt: parseTs(v?.lastSeenAt),
              battery: typeof v?.battery === 'number' ? v.battery : null,
              // Explicit logout marker (kept alongside lastSeenAt/battery so those
              // still display for a logged-out driver).
              loggedOutAt: parseTs(v?.loggedOutAt),
              // Why the device is reachable; null for pre-field app builds, which
              // usePresence then treats as online (the prior behaviour).
              appState: parseAppState(v?.appState)
            };
            onUpdate({ ...store });
          },
          () => {
            // Permission denied / transient error — leave the entry absent so the
            // consumer falls back to whatever the REST snapshot gave it.
          }
        )
      );

    return () => offs.forEach((fn) => fn());
  };

  return (
    <FirebaseContext.Provider
      value={{
        app,
        database,
        firebaseUid,
        addCallData,
        getCallData,
        updateCallStatus,
        addCallHistoryEntry,
        getMyOngoingCalls,
        updateSessionData,
        addCallToUser,
        addSessionUsers,
        updateMuteStatus,
        addScreenshot,
        updateLocationStatus,
        updateRecordingStatus,
        updateFromName,
        pushMessage,
        updateRecipients,
        markMessagesAsRead,
        updateSessionTokens,
        removeUserUidFromTo,
        updateUserCallStatus,
        updateCallSupportInitiatedBy,
        getMessageHistory,
        getLocationData,
        getAllUsersLocationData,
        subscribeToTransactionLocations,
        subscribeToUserActivity
      }}
    >
      {children}
    </FirebaseContext.Provider>
  );
};

export default FirebaseContext;
